import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import LanguageToggle from '../../components/LanguageToggle.jsx';
import NewsletterForm from '../../components/NewsletterForm.jsx';
import SocialIcons from '../../components/SocialIcons.jsx';
import { book } from '../../content/book.js';
import { variants } from './variants.jsx';

const ease = [0.16, 1, 0.3, 1];

const PIGI_URL = book.retailers.find((r) => r.id === 'pigi')?.url || '#';

// Section backgrounds — clean dark, no colour cast
const AUTHOR_BG =
  'linear-gradient(180deg, oklch(0.10 0.008 25) 0%, oklch(0.13 0.010 250) 14%, oklch(0.13 0.010 250) 86%, oklch(0.10 0.008 25) 100%)';
const NEWSLETTER_BG =
  'linear-gradient(180deg, oklch(0.10 0.008 25) 0%, oklch(0.12 0.008 25) 12%, oklch(0.12 0.008 25) 88%, oklch(0.10 0.008 25) 100%)';
const BUY_BG =
  'linear-gradient(180deg, oklch(0.10 0.008 25) 0%, oklch(0.13 0.008 25) 14%, oklch(0.13 0.008 25) 86%, oklch(0.10 0.008 25) 100%)';

// Pre-computed ember particle data (avoids re-creation on render)
const EMBER_ANIMS = ['ember-rise-a', 'ember-rise-b', 'ember-rise-c', 'ember-rise-d'];
const EMBER_COLORS = [
  'oklch(0.84 0.060 20)',   // silver-warm
  'oklch(0.55 0.140 22)',   // burgundy
  'oklch(0.72 0.100 28)',   // warm amber
  'oklch(0.90 0.012 250)',  // cool silver
];
const EMBERS = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  left: `${4 + ((i * 4.7 + i * i * 0.15) % 90)}%`,
  size: 1 + (i % 3),
  duration: 9 + ((i * 1.4) % 13),
  delay: (i * 0.65) % 10,
  anim: EMBER_ANIMS[i % 4],
  color: EMBER_COLORS[i % 4],
}));

// ─── Decorative SVG / particle components ──────────────────────────────────

function EmberParticles({ reduce }) {
  if (reduce) return null;
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      {EMBERS.map((p) => (
        <div
          key={p.id}
          className="absolute bottom-0 rounded-full"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 ${p.size * 5}px ${p.color}, 0 0 ${p.size * 10}px ${p.color}40`,
            animation: `${p.anim} ${p.duration}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function DistantLandscape({ reduce }) {
  return (
    <svg
      aria-hidden
      className="absolute bottom-0 left-0 w-full pointer-events-none select-none"
      viewBox="0 0 1600 280"
      preserveAspectRatio="xMidYMax slice"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Far mountains */}
      <path
        d="M0 280 L0 195 L90 155 L180 178 L310 118 L440 148 L570 105 L700 138 L830 98 L960 128 L1090 88 L1220 118 L1350 82 L1480 108 L1600 95 L1600 280 Z"
        fill="oklch(0.16 0.010 28 / 0.30)"
      />
      {/* Mid mountains */}
      <path
        d="M0 280 L0 235 L130 188 L260 218 L390 168 L520 198 L650 158 L780 182 L910 148 L1040 174 L1170 142 L1300 165 L1430 138 L1600 152 L1600 280 Z"
        fill="oklch(0.12 0.012 26 / 0.55)"
      />
      {/* Foreground silhouette with ruins */}
      <path
        d="M0 280 L0 265 L180 260 L180 255 L220 255 L220 248 L230 248 L230 255 L270 255 L270 260 L480 258 L480 252 L490 240 L500 252 L510 258 L700 256 L740 244 L780 256 L900 254 L900 260 L1200 258 L1600 260 L1600 280 Z"
        fill="oklch(0.09 0.014 25 / 0.85)"
      />
      {/* Column ruins */}
      <rect x="52"  y="218" width="7" height="47" fill="oklch(0.22 0.012 28 / 0.55)" />
      <rect x="47"  y="214" width="17" height="5" fill="oklch(0.24 0.012 28 / 0.55)" />
      <rect x="68"  y="230" width="5" height="35" fill="oklch(0.20 0.010 28 / 0.45)" />
      <rect x="65"  y="226" width="11" height="4" fill="oklch(0.20 0.010 28 / 0.45)" />
      <rect x="110" y="225" width="6" height="40" fill="oklch(0.21 0.011 28 / 0.50)" />
      <rect x="107" y="221" width="12" height="4" fill="oklch(0.21 0.011 28 / 0.50)" />
      {/* Right side ruins */}
      <rect x="1490" y="220" width="8" height="45" fill="oklch(0.20 0.010 28 / 0.45)" />
      <rect x="1486" y="216" width="16" height="4" fill="oklch(0.20 0.010 28 / 0.45)" />
      <rect x="1510" y="232" width="5" height="33" fill="oklch(0.18 0.009 28 / 0.40)" />
      <rect x="1507" y="228" width="11" height="4" fill="oklch(0.18 0.009 28 / 0.40)" />
      {/* Distant ruins */}
      <rect x="780" y="235" width="5" height="30" fill="oklch(0.18 0.009 28 / 0.35)" />
      <rect x="777" y="231" width="11" height="4" fill="oklch(0.18 0.009 28 / 0.35)" />
      <rect x="795" y="240" width="4" height="22" fill="oklch(0.17 0.009 28 / 0.30)" />
      {!reduce && (
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0 0; 0 -4; 0 0"
          dur="18s"
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.45 0 0.55 1; 0.45 0 0.55 1"
        />
      )}
    </svg>
  );
}

function HeroWispDecor() {
  return (
    <svg
      aria-hidden
      className="absolute bottom-0 left-0 w-[65vw] max-w-[700px] opacity-[0.07] pointer-events-none select-none"
      viewBox="0 0 600 700"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M-20 700 C80 520 30 340 140 180 C200 90 320 60 380 -20" stroke="oklch(0.84 0.060 20)" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M40 700 C130 540 90 370 190 210 C240 130 350 100 400 20" stroke="oklch(0.84 0.060 20)" strokeWidth="1.0" strokeLinecap="round" />
      <path d="M100 700 C180 560 150 400 240 240 C280 170 370 140 420 60" stroke="oklch(0.42 0.140 22)" strokeWidth="0.8" strokeLinecap="round" />
      <path d="M160 700 C230 580 200 430 290 280 C330 210 400 180 450 100" stroke="oklch(0.84 0.060 20)" strokeWidth="0.6" strokeLinecap="round" />
      <path d="M220 700 C280 600 260 460 340 320 C380 250 440 220 480 150" stroke="oklch(0.84 0.060 20)" strokeWidth="0.5" strokeLinecap="round" />
    </svg>
  );
}

function HeroCornerDecor() {
  return (
    <svg
      aria-hidden
      className="absolute top-10 right-0 w-[38vw] max-w-[480px] opacity-[0.055] pointer-events-none select-none"
      viewBox="0 0 480 480"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="420" cy="60" r="220" stroke="oklch(0.84 0.060 20)" strokeWidth="0.8" />
      <circle cx="420" cy="60" r="170" stroke="oklch(0.84 0.060 20)" strokeWidth="0.5" />
      <circle cx="420" cy="60" r="120" stroke="oklch(0.42 0.140 22)" strokeWidth="0.5" />
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        return (
          <line
            key={deg}
            x1={420} y1={60}
            x2={420 + Math.cos(rad) * 500}
            y2={60 + Math.sin(rad) * 500}
            stroke="oklch(0.84 0.060 20)"
            strokeWidth="0.4"
          />
        );
      })}
    </svg>
  );
}

function BookSectionDecor() {
  return (
    <>
      {/* Warm radial rays from behind the cover */}
      <svg
        aria-hidden
        className="absolute right-0 top-0 w-[55vw] max-w-[650px] h-full opacity-[0.055] pointer-events-none select-none"
        viewBox="0 0 650 700"
        preserveAspectRatio="xMaxYMid meet"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {[0, 20, 40, 60, 80, 100, 120, 140, 160, 200, 240, 280, 320].map((deg) => {
          const rad = (deg * Math.PI) / 180;
          return (
            <line
              key={deg}
              x1={580} y1={350}
              x2={580 + Math.cos(rad) * 800}
              y2={350 + Math.sin(rad) * 800}
              stroke="oklch(0.78 0.060 40)"
              strokeWidth="0.7"
            />
          );
        })}
        <circle cx="580" cy="350" r="100" stroke="oklch(0.78 0.060 40)" strokeWidth="0.8" fill="none" />
        <circle cx="580" cy="350" r="200" stroke="oklch(0.78 0.060 40)" strokeWidth="0.5" fill="none" />
        <circle cx="580" cy="350" r="340" stroke="oklch(0.78 0.060 40)" strokeWidth="0.3" fill="none" />
      </svg>
    </>
  );
}

function AuthorSectionDecor() {
  return (
    <img
      src="/decor/botanical.svg"
      aria-hidden
      className="absolute left-0 top-0 h-full w-auto max-w-[28%] pointer-events-none select-none object-contain object-left-top"
      style={{ opacity: 0.22, mixBlendMode: 'screen' }}
      alt=""
    />
  );
}

function ColumnsDecor() {
  const col = (x) => (
    <g key={x}>
      <rect x={x} y={30} width={32} height={620} stroke="oklch(0.72 0.012 60)" strokeWidth="0.5" fill="none" />
      {[7, 14, 21].map((off) => (
        <line key={off} x1={x + off} y1={50} x2={x + off} y2={630} stroke="oklch(0.72 0.012 60)" strokeWidth="0.3" opacity="0.6" />
      ))}
      <rect x={x - 8} y={10}  width={48} height={20} stroke="oklch(0.72 0.012 60)" strokeWidth="0.5" fill="none" />
      <rect x={x - 4} y={30}  width={40} height={8}  stroke="oklch(0.72 0.012 60)" strokeWidth="0.4" fill="none" opacity="0.7" />
      <rect x={x - 8} y={630} width={48} height={20} stroke="oklch(0.72 0.012 60)" strokeWidth="0.5" fill="none" />
    </g>
  );
  return (
    <svg
      aria-hidden
      className="absolute left-0 top-0 h-full w-[12vw] max-w-[140px] opacity-[0.08] pointer-events-none select-none"
      viewBox="0 0 140 700"
      preserveAspectRatio="xMinYMid meet"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {col(20)}
      {col(80)}
    </svg>
  );
}

function StarsDecor({ reduce }) {
  const pts = [
    [60, 40], [180, 25], [310, 60], [430, 20], [560, 55], [700, 35],
    [90, 130], [240, 100], [380, 140], [510, 90], [650, 120],
    [40, 220], [170, 200], [300, 240], [460, 190], [600, 210],
    [120, 310], [260, 290], [400, 330], [530, 270], [680, 300],
    [80, 380], [220, 360], [350, 390], [490, 350],
  ];
  const constellations = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5],
    [6, 7], [7, 8], [8, 9],
    [10, 11], [11, 12], [12, 13],
    [14, 15], [16, 17], [17, 18],
    [1, 6], [3, 8], [5, 10],
  ];
  return (
    <svg
      aria-hidden
      className="absolute inset-0 w-full h-full opacity-[0.20] pointer-events-none select-none"
      viewBox="0 0 800 420"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {constellations.map(([a, b], i) => (
        <line
          key={i}
          x1={pts[a][0]} y1={pts[a][1]}
          x2={pts[b][0]} y2={pts[b][1]}
          stroke="oklch(0.84 0.010 250)"
          strokeWidth="0.4"
          opacity="0.4"
        />
      ))}
      {pts.map(([x, y], i) => (
        <circle
          key={i}
          cx={x} cy={y}
          r={i % 5 === 0 ? 2 : i % 3 === 0 ? 1.5 : 1}
          fill="oklch(0.90 0.008 250)"
          style={
            !reduce
              ? {
                  animation: `${i % 2 === 0 ? 'star-twinkle' : 'star-twinkle-b'} ${2.5 + ((i * 0.37) % 3)}s ease-in-out ${(i * 0.28) % 2.5}s infinite`,
                }
              : undefined
          }
        />
      ))}
    </svg>
  );
}

function MapGridDecor() {
  const cols = 10, rows = 6, w = 800, h = 500;
  const cw = w / cols, rh = h / rows;
  return (
    <svg
      aria-hidden
      className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none select-none"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {Array.from({ length: cols + 1 }).map((_, i) => (
        <line key={`c${i}`} x1={i * cw} y1="0" x2={i * cw} y2={h} stroke="oklch(0.78 0.040 50)" strokeWidth="0.5" />
      ))}
      {Array.from({ length: rows + 1 }).map((_, i) => (
        <line key={`r${i}`} x1="0" y1={i * rh} x2={w} y2={i * rh} stroke="oklch(0.78 0.040 50)" strokeWidth="0.5" />
      ))}
      <circle cx="680" cy="380" r="55" stroke="oklch(0.78 0.040 50)" strokeWidth="0.6" fill="none" opacity="0.8" />
      <circle cx="680" cy="380" r="35" stroke="oklch(0.78 0.040 50)" strokeWidth="0.4" fill="none" opacity="0.5" />
      <line x1="680" y1="325" x2="680" y2="435" stroke="oklch(0.78 0.040 50)" strokeWidth="0.5" opacity="0.8" />
      <line x1="625" y1="380" x2="735" y2="380" stroke="oklch(0.78 0.040 50)" strokeWidth="0.5" opacity="0.8" />
      <line x1="641" y1="341" x2="719" y2="419" stroke="oklch(0.78 0.040 50)" strokeWidth="0.4" opacity="0.4" />
      <line x1="719" y1="341" x2="641" y2="419" stroke="oklch(0.78 0.040 50)" strokeWidth="0.4" opacity="0.4" />
      <ellipse cx="400" cy="250" rx="350" ry="200" stroke="oklch(0.78 0.040 50)" strokeWidth="0.5" fill="none" opacity="0.15" />
    </svg>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

function resolveIsGreek(lang) {
  const l = (lang || '').toLowerCase();
  return l.startsWith('gr') || l.startsWith('el');
}

export default function RequiemNoir({ variant = 'atelier' }) {
  const { t, i18n } = useTranslation();
  const reduce = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const v = variants[variant] || variants.atelier;

  const isGreek = resolveIsGreek(i18n.resolvedLanguage);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.lang = (i18n.resolvedLanguage || 'en').slice(0, 2);
  }, [i18n.resolvedLanguage]);

  const year = new Date().getFullYear();

  return (
    <div
      data-variant={variant}
      className="relative min-h-screen bg-ink-deeper text-silver-100 font-noirBody antialiased overflow-x-clip"
    >
      <Backdrop v={v} reduce={reduce} />
      <Nav scrolled={scrolled} navOpen={navOpen} setNavOpen={setNavOpen} t={t} v={v} isGreek={isGreek} />

      <main className="relative z-10">
        <Hero reduce={reduce} t={t} i18n={i18n} v={v} isGreek={isGreek} />

        {isGreek ? (
          <>
            <Ribbon v={v} />
            <ActFrame label={t('nav.book')} id="book" v={v} tone="featured" decor={null}>
              <BookSection t={t} reduce={reduce} v={v} isGreek={isGreek} />
            </ActFrame>
            <ActFrame label={t('nav.author')} id="author" v={v} sectionBg={AUTHOR_BG} decor={null}>
              <AuthorSection t={t} reduce={reduce} />
            </ActFrame>
            <ActFrame label={t('nav.newsletter')} id="newsletter" v={v} sectionBg={NEWSLETTER_BG} decor={null}>
              <NewsletterSection t={t} reduce={reduce} />
            </ActFrame>
            <ActFrame label={t('nav.buy')} id="buy" last v={v} sectionBg={BUY_BG} decor={null}>
              <BuySection t={t} reduce={reduce} v={v} />
            </ActFrame>
          </>
        ) : (
          <>
            <ActFrame label="" id="newsletter" v={v} sectionBg={NEWSLETTER_BG} last decor={null}>
              <NewsletterSection t={t} reduce={reduce} />
            </ActFrame>
          </>
        )}
      </main>

      <Footer t={t} year={year} isGreek={isGreek} />
    </div>
  );
}

// ─── Backdrop with parallax + breathing glows + fog ───────────────────────────

function Backdrop({ v, reduce }) {
  const { scrollY } = useScroll();
  const coverY = useTransform(scrollY, [0, 1200], ['0%', reduce ? '0%' : '28%']);

  const glowAOpacity = v.backdrop.glowAOpacity;
  const glowBOpacity = v.backdrop.glowBOpacity;

  return (
    <div aria-hidden className="fixed inset-0 z-0 pointer-events-none">
      {/* Base gradient */}
      <div className="absolute inset-0" style={{ background: v.backdrop.base }} />

      {/* Blurred book cover — parallax layer */}
      <motion.div
        className="absolute inset-0 mix-blend-screen"
        style={{
          y: coverY,
          opacity: v.backdrop.coverBlurOpacity,
          backgroundImage: "url('/covers/cover-book.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          filter: 'blur(34px) saturate(0.7)',
        }}
      />

      {/* Fog layers — horizontal drift */}
      {!reduce && (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse 130% 55% at 15% 65%, oklch(0.18 0.018 25 / 0.22) 0%, transparent 68%)',
              animation: 'fog-drift 24s ease-in-out infinite',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse 110% 45% at 82% 38%, oklch(0.14 0.010 250 / 0.18) 0%, transparent 60%)',
              animation: 'fog-drift-alt 31s ease-in-out infinite',
              animationDelay: '-8s',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse 90% 40% at 50% 82%, oklch(0.20 0.025 22 / 0.14) 0%, transparent 55%)',
              animation: 'fog-drift 19s ease-in-out infinite',
              animationDelay: '-14s',
            }}
          />
        </>
      )}

      {/* Breathing glow A — top-left burgundy */}
      <motion.div
        className="absolute -top-40 -left-40 w-[60vw] h-[60vw] rounded-full"
        style={{ background: v.backdrop.glowA }}
        animate={reduce ? undefined : {
          opacity: [glowAOpacity * 0.65, glowAOpacity, glowAOpacity * 0.70],
          scale:   [1, 1.10, 1],
        }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        initial={{ opacity: glowAOpacity }}
      />

      {/* Breathing glow B — bottom-right silver */}
      <motion.div
        className="absolute -bottom-40 -right-40 w-[55vw] h-[55vw] rounded-full"
        style={{ background: v.backdrop.glowB }}
        animate={reduce ? undefined : {
          opacity: [glowBOpacity * 0.60, glowBOpacity, glowBOpacity * 0.65],
          scale:   [1, 1.12, 1],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
        initial={{ opacity: glowBOpacity }}
      />

      {v.backdrop.vignette && (
        <div className="absolute inset-0" style={{ background: v.backdrop.vignette }} />
      )}
      {v.backdrop.texture && (
        <div
          className="absolute inset-0"
          style={{ backgroundImage: v.backdrop.texture, opacity: v.backdrop.textureOpacity, mixBlendMode: 'overlay' }}
        />
      )}
      <div className="absolute inset-0 grain-overlay" style={{ opacity: v.backdrop.grainOpacity }} />
    </div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

function Nav({ scrolled, navOpen, setNavOpen, t, v, isGreek }) {
  const links = isGreek
    ? [
        { href: '#book',       label: t('nav.book') },
        { href: '#author',     label: t('nav.author') },
        { href: '#newsletter', label: t('nav.newsletter') },
        { href: '#buy',        label: t('nav.buy') },
      ]
    : [
        { href: '#newsletter', label: t('nav.newsletter') },
      ];
  return (
    <header
      className={`fixed top-0 inset-x-0 z-30 transition-all duration-700 ease-out-quart
        ${scrolled || navOpen
          ? 'bg-ink-deeper/85 backdrop-blur-md border-b border-silver-500/15'
          : 'bg-transparent border-b border-transparent'}`}
    >
      <div className="px-5 sm:px-10 lg:px-20 xl:px-28 h-14 flex items-center justify-between gap-4">
        <a
          href="#top"
          className="font-trajan text-silver-100 text-base sm:text-lg tracking-wide hover:text-silver-50 transition-colors"
        >
          Requiem.
        </a>
        <nav className="hidden md:flex items-center gap-8 lg:gap-9 text-silver-300 text-[0.7rem] uppercase tracking-widest2">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="hover:text-silver-50 transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3 sm:gap-4">
          <LanguageToggle />
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((x) => !x)}
            className="md:hidden p-2 -mr-2 text-silver-300 hover:text-silver-50 transition-colors"
          >
            <svg width="20" height="14" viewBox="0 0 20 14" aria-hidden>
              <line x1="0" y1="2" x2="20" y2="2" stroke="currentColor" strokeWidth="1.25" />
              <line
                x1="0" y1="7" x2="20" y2="7"
                stroke="currentColor" strokeWidth="1.25"
                style={{
                  transformOrigin: 'center',
                  transform: navOpen ? 'scaleX(0.5)' : 'scaleX(1)',
                  transition: 'transform 300ms cubic-bezier(0.25,1,0.5,1)',
                }}
              />
              <line x1="0" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="1.25" />
            </svg>
          </button>
        </div>
      </div>
      <div
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out-quart ${
          navOpen ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="px-5 pb-5 pt-1 flex flex-col gap-1 text-silver-300 text-[0.78rem] uppercase tracking-widest2">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setNavOpen(false)}
              className="flex items-baseline gap-3 py-2 border-b border-silver-500/10 hover:text-silver-50 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero({ reduce, t, i18n, v, isGreek }) {
  const motionProps = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.12 } }
    : { initial: { opacity: 0, y: 22 }, animate: { opacity: 1, y: 0 } };

  return (
    <section
      id="top"
      className="relative overflow-hidden min-h-[100svh] flex flex-col justify-between px-5 sm:px-10 lg:px-20 xl:px-28 pt-24 pb-10 sm:pb-12"
    >
      <EmberParticles reduce={reduce} />

      <div className="relative flex-1 flex flex-col justify-center max-w-[88rem] mx-auto w-full">
        {/* Author name block — always visible */}
        <motion.div
          {...motionProps}
          transition={{ ...motionProps.transition, duration: reduce ? 0.12 : 1.0, delay: 0.05, ease }}
          className="mb-6 sm:mb-7"
        >
          {/* Show eyebrow only when it has content (English has it; Greek is empty) */}
          {t('hero.eyebrow') && (
            <p className="text-silver-400 text-[0.66rem] sm:text-[0.72rem] uppercase tracking-widest2">
              {t('hero.eyebrow')}
            </p>
          )}
          <p className={`font-noir italic text-silver-100 text-base sm:text-lg ${t('hero.eyebrow') ? 'mt-1' : ''}`}>
            {t('meta.authorName')}
          </p>
        </motion.div>

        {isGreek ? (
          <motion.div
            {...motionProps}
            transition={{ ...motionProps.transition, duration: reduce ? 0.12 : 1.4, delay: 0.18, ease }}
          >
            <h1 className="sr-only">{t('meta.seriesName')}</h1>
            <img
              src="/book-logo.png"
              alt={t('meta.seriesName')}
              className="h-auto select-none w-full"
              style={{
                maxWidth: 'min(82%, 38rem)',
                marginLeft: '-0.25rem',
                filter: 'drop-shadow(0 18px 40px oklch(0 0 0 / 0.45))',
              }}
              draggable={false}
            />
          </motion.div>
        ) : (
          <motion.h1
            {...motionProps}
            transition={{ ...motionProps.transition, duration: reduce ? 0.12 : 1.4, delay: 0.18, ease }}
            className="font-trajan text-silver-50 leading-[0.9] tracking-[0.02em] text-balance uppercase"
            style={{ fontSize: 'clamp(2.5rem, 11vw, 11rem)' }}
          >
            <span className="block font-light">{firstWord(t('meta.seriesName'))}</span>
            <span className="block font-semibold">{restWords(t('meta.seriesName'))}</span>
          </motion.h1>
        )}

        {/* Book one + subtitle block — Greek full; English Coming Soon */}
        <motion.div
          {...motionProps}
          transition={{ ...motionProps.transition, duration: reduce ? 0.12 : 1.2, delay: 0.55, ease }}
          className="mt-9 sm:mt-12 grid grid-cols-1 sm:grid-cols-12 gap-5 sm:gap-6 items-end"
        >
          {isGreek ? (
            <>
              <div className="sm:col-span-6">
                <p className="text-silver-500 text-[0.6rem] sm:text-[0.66rem] uppercase tracking-widest3 mb-2">
                  {t('hero.bookOneLabel')}
                </p>
                <p className="font-noir text-silver-50 text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05]">
                  {t('meta.bookOne')}
                </p>
              </div>
              <div className="sm:col-span-6 sm:pl-10 sm:border-l border-silver-500/30">
                <p
                  className="text-silver-300 text-[0.95rem] sm:text-base md:text-lg leading-relaxed text-pretty"
                  style={{ maxWidth: '40ch' }}
                >
                  {t('hero.tagline')}
                </p>
              </div>
            </>
          ) : (
            <div className="sm:col-span-8">
              <p className="text-silver-500 text-[0.6rem] sm:text-[0.66rem] uppercase tracking-widest3 mb-3">
                Book One
              </p>
              <p className="font-noir italic text-silver-300 text-xl sm:text-2xl leading-relaxed">
                Cover Reveal Coming Soon
              </p>
              <p className="mt-4 text-silver-400 text-sm leading-relaxed" style={{ maxWidth: '40ch' }}>
                {t('hero.tagline')}
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Bottom bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1 }}
        className="relative mt-10 sm:mt-12 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 text-silver-500 text-[0.58rem] sm:text-[0.62rem] uppercase tracking-[0.32em] sm:tracking-widest3 border-t border-silver-500/20 pt-5"
      >
        <span>{t('meta.genre')}</span>
        {isGreek && (
          <a href="#book" className="hover:text-silver-100 transition-colors duration-300">
            ↓ {t('nav.book')}
          </a>
        )}
      </motion.div>
    </section>
  );
}

function firstWord(s) {
  const parts = (s || '').trim().split(' ');
  return parts.slice(0, 2).join(' ');
}
function restWords(s) {
  const parts = (s || '').trim().split(' ');
  return parts.slice(2).join(' ');
}

// ─── Ribbon divider ───────────────────────────────────────────────────────────

function Ribbon({ v }) {
  return (
    <div aria-hidden className="relative h-20 flex items-center">
      <div className="absolute inset-x-0 top-1/2 h-px" style={{ background: v.divider.line }} />
      <div className="relative mx-auto flex items-center gap-4 bg-ink-deeper px-6">
        {v.divider.ornament}
      </div>
    </div>
  );
}

// ─── ActFrame — section wrapper ───────────────────────────────────────────────

function ActFrame({ label, id, children, last, v, tone, sectionBg, decor }) {
  const featured = tone === 'featured';
  const bgStyle =
    featured && v.section?.featuredBg
      ? { background: v.section.featuredBg }
      : sectionBg
      ? { background: sectionBg }
      : undefined;

  return (
    <section
      id={id}
      className={`relative overflow-hidden px-5 sm:px-10 lg:px-20 xl:px-28 py-16 sm:py-20 md:py-24 ${
        last ? 'pb-24 sm:pb-28 md:pb-32' : ''
      } scroll-mt-20`}
      style={bgStyle}
    >
      {featured && v.section?.featuredOverlay && (
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: v.section.featuredOverlay }} />
      )}
      {decor && (
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          {decor}
        </div>
      )}
      <div className="relative max-w-[88rem] mx-auto">
        <p className="text-silver-500 text-[0.62rem] sm:text-[0.66rem] uppercase tracking-widest3 mb-6 sm:mb-8">
          {label}
        </p>
        {children}
      </div>
    </section>
  );
}

// ─── Shared reveal animation ──────────────────────────────────────────────────

function fadeInView(reduce, delay = 0) {
  if (reduce) {
    return {
      initial: { opacity: 0 },
      whileInView: { opacity: 1 },
      transition: { duration: 0.12 },
      viewport: { once: true, amount: 0.2 },
    };
  }
  return {
    initial: { opacity: 0, y: 28, filter: 'blur(8px)' },
    whileInView: { opacity: 1, y: 0, filter: 'blur(0px)' },
    transition: { duration: 1.2, delay, ease },
    viewport: { once: true, amount: 0.15 },
  };
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function BookSection({ t, reduce, v, isGreek }) {
  return (
    <div className="grid grid-cols-12 gap-x-6 sm:gap-x-10 gap-y-12 sm:gap-y-14 items-center">
      <motion.div
        className="col-span-12 md:col-span-6 order-1 md:order-2 flex justify-center"
        {...fadeInView(reduce, 0.05)}
      >
        <CoverFrame t={t} v={v} isGreek={isGreek} />
      </motion.div>
      <motion.div className="col-span-12 md:col-span-6 order-2 md:order-1" {...fadeInView(reduce, 0.12)}>
        {isGreek ? (
          <>
            <h2
              className="font-noir text-silver-50 leading-[1.0] tracking-[-0.02em] text-balance"
              style={{ fontSize: 'clamp(2rem, 5.6vw, 4.6rem)' }}
            >
              {t('book.heading')}
            </h2>
            <p
              className="mt-6 sm:mt-8 font-noir italic text-silver-100 text-lg sm:text-xl leading-[1.55] text-pretty border-l-2 border-burgundy-300/70 pl-5"
              style={{ maxWidth: '54ch' }}
            >
              {t('book.blurb')}
            </p>
            <div
              className="mt-7 text-silver-200 text-[1rem] sm:text-[1.05rem] leading-[1.75] text-pretty whitespace-pre-line"
              style={{ maxWidth: '54ch' }}
            >
              {t('book.blurbBody')}
            </div>
            {t('book.blurbCoda') && (
              <p
                className="mt-7 font-noir italic text-silver-300 text-[1rem] sm:text-lg leading-relaxed text-pretty"
                style={{ maxWidth: '52ch' }}
              >
                {t('book.blurbCoda')}
              </p>
            )}
            <div className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href={PIGI_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`group inline-flex items-center gap-3 px-6 py-3.5 text-[0.72rem] uppercase tracking-widest2 transition-all duration-500 ease-out-quart ${v.button.primary}`}
                style={v.button.primaryStyle}
              >
                <span>{t('hero.buyNow')}</span>
                <span aria-hidden className="inline-block translate-y-[-1px] transition-transform duration-500 ease-out-quart group-hover:translate-x-1">→</span>
              </a>
              {book.sample.enabled && (
                <a
                  href={book.sample.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group inline-flex items-center gap-3 px-5 py-3.5 text-[0.72rem] uppercase tracking-widest2 transition-all duration-500 ease-out-quart ${v.button.secondary}`}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
                    <path d="M7 1v8m0 0L3.5 5.5M7 9l3.5-3.5M1 12h12" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                  </svg>
                  <span>{t('hero.sample')}</span>
                  <span aria-hidden className="inline-block translate-y-[-1px] transition-transform duration-500 ease-out-quart group-hover:translate-x-1">→</span>
                </a>
              )}
            </div>
          </>
        ) : (
          <>
            <h2
              className="font-noir text-silver-50 leading-[1.0] tracking-[-0.02em] text-balance"
              style={{ fontSize: 'clamp(2rem, 5.6vw, 4.6rem)' }}
            >
              {t('book.heading')}
            </h2>
            <p className="mt-6 sm:mt-8 text-silver-300 text-base sm:text-lg leading-relaxed text-pretty" style={{ maxWidth: '48ch' }}>
              The cover reveal is coming soon. Join the newsletter to be the first to see it when it arrives.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}

function CoverFrame({ t, v, isGreek }) {
  return (
    <div className="relative w-full max-w-[32rem] sm:max-w-[44rem] md:max-w-[54rem] lg:max-w-[60rem]">
      <div aria-hidden className="absolute -inset-16 sm:-inset-20 pointer-events-none" style={{ background: v.cover.outerGlow, opacity: v.cover.outerGlowOpacity }} />
      <div aria-hidden className="absolute -inset-4 pointer-events-none" style={{ background: v.cover.innerGlow, opacity: v.cover.innerGlowOpacity }} />
      {v.cover.frameRing && (
        <div aria-hidden className="absolute -inset-6 pointer-events-none rounded-[2px]" style={{ border: v.cover.frameRing }} />
      )}
      <figure className="relative">
        {isGreek ? (
          <a
            href={PIGI_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${book.cover.alt} — ${t('hero.buyNow')}`}
            className="block group transition-transform duration-700 ease-out-quart hover:-translate-y-1"
          >
            <img
              src={book.cover.mockupSrc}
              alt={book.cover.alt}
              loading="lazy"
              className="relative w-full h-auto select-none"
              style={{ filter: v.cover.imgShadow }}
            />
          </a>
        ) : (
          <div className="relative" style={{ perspective: '2000px' }}>
            <div 
              className="relative w-full"
              style={{
                aspectRatio: '2/3',
                transformStyle: 'preserve-3d',
                transform: 'rotateY(-25deg) rotateX(5deg)',
              }}
            >
              {/* Main book cover */}
              <div
                className="absolute inset-0 flex flex-col items-center justify-center border-r-2 border-silver-500/30"
                style={{
                  background: 'linear-gradient(135deg, oklch(0.12 0.004 250) 0%, oklch(0.08 0.003 250) 100%)',
                  boxShadow: `
                    0 50px 100px -20px rgba(0, 0, 0, 0.8),
                    0 30px 60px -30px rgba(0, 0, 0, 0.6),
                    inset -8px 0 20px -8px rgba(0, 0, 0, 0.4)
                  `,
                }}
              >
                <p className="font-noir italic text-silver-200 text-2xl sm:text-3xl md:text-4xl text-center px-8 leading-relaxed">
                  Cover Reveal<br />Coming Soon
                </p>
              </div>
              
              {/* Spine */}
              <div
                className="absolute top-0 bottom-0 -right-[3%] w-[3%]"
                style={{
                  transform: 'rotateY(90deg)',
                  transformOrigin: 'left center',
                  background: 'linear-gradient(to right, oklch(0.06 0.003 250), oklch(0.04 0.002 250))',
                  boxShadow: 'inset 2px 0 8px rgba(0, 0, 0, 0.6)',
                }}
              />
              
              {/* Pages - right side */}
              <div
                className="absolute top-[2%] bottom-[2%] -right-[2.5%] w-[2.5%]"
                style={{
                  transform: 'rotateY(90deg)',
                  transformOrigin: 'left center',
                  background: 'linear-gradient(to right, oklch(0.85 0.002 60), oklch(0.88 0.002 60))',
                  boxShadow: 'inset -2px 0 4px rgba(0, 0, 0, 0.3)',
                }}
              />
              
              {/* Pages - visible edge lines */}
              <div
                className="absolute top-[2%] bottom-[2%] -right-[2.5%] w-[2.5%] pointer-events-none"
                style={{
                  transform: 'rotateY(90deg)',
                  transformOrigin: 'left center',
                  background: `repeating-linear-gradient(
                    to bottom,
                    transparent 0px,
                    transparent 2px,
                    rgba(0, 0, 0, 0.1) 2px,
                    rgba(0, 0, 0, 0.1) 3px
                  )`,
                }}
              />
            </div>
          </div>
        )}
        <figcaption className="mt-5 text-silver-500 text-[0.65rem] uppercase tracking-widest3 text-center">
          {isGreek ? t('book.coverCaption') : 'English edition · Coming soon'}
        </figcaption>
      </figure>
    </div>
  );
}

function AuthorSection({ t, reduce }) {
  const bioParagraphs = (t('author.bio') || '').split('\n\n').filter(Boolean);

  return (
    <motion.div
      {...fadeInView(reduce, 0.05)}
      className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-10 items-start"
    >
      {/* Author photo */}
      <div className="md:col-span-4 lg:col-span-3">
        <div
          className="overflow-hidden border border-silver-500/15"
          style={{ maxWidth: '260px', aspectRatio: '3/4' }}
        >
          <img
            src="/author.jpg"
            alt={t('meta.authorName')}
            loading="lazy"
            className="w-full h-full object-cover object-top select-none"
          />
        </div>
      </div>

      {/* Text */}
      <div className="md:col-span-8 lg:col-span-9">
        <h2
          className="font-noir text-silver-50 leading-[1.05] tracking-[-0.02em] text-balance"
          style={{ fontSize: 'clamp(1.9rem, 4.8vw, 3.6rem)' }}
        >
          {t('meta.authorName')}
        </h2>
        <div className="mt-7 sm:mt-9 flex flex-col gap-5">
          {bioParagraphs.map((para, i) => (
            <p
              key={i}
              className="text-silver-200 text-base sm:text-lg leading-[1.85] text-pretty"
              style={{ maxWidth: '60ch' }}
            >
              {para}
            </p>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function NewsletterSection({ t, reduce }) {
  return (
    <motion.div
      className="grid grid-cols-12 gap-x-6 sm:gap-x-10 gap-y-8 items-end"
      {...fadeInView(reduce, 0.05)}
    >
      <div className="col-span-12 md:col-span-6">
        <h2
          className="font-noir text-silver-50 leading-[1.0] tracking-[-0.02em] text-balance"
          style={{ fontSize: 'clamp(2rem, 5.6vw, 4.4rem)' }}
        >
          {t('newsletter.heading')}
        </h2>
        <p className="mt-6 sm:mt-7 text-silver-300 text-[0.95rem] sm:text-base leading-relaxed text-pretty" style={{ maxWidth: '40ch' }}>
          {t('newsletter.body')}
        </p>
      </div>
      <div className="col-span-12 md:col-span-6">
        <NewsletterForm
          classes={{
            form: 'flex flex-col gap-3',
            inputWrap: 'flex flex-col sm:flex-row sm:items-stretch border-y border-silver-500/35 focus-within:border-silver-200 transition-colors duration-300',
            label: 'sr-only',
            input: 'flex-1 min-w-0 bg-transparent px-1 sm:px-2 py-4 text-silver-50 placeholder:text-silver-500 font-noir italic font-light text-xl sm:text-2xl focus:outline-none',
            button: 'shrink-0 sm:ml-6 self-stretch px-5 sm:px-6 py-3 text-silver-50 hover:text-burgundy-100 text-[0.7rem] sm:text-[0.72rem] uppercase tracking-widest2 transition-colors text-left sm:text-right',
            privacy: 'text-silver-500 text-xs',
            success: 'font-noir italic text-silver-100 text-xl sm:text-2xl leading-relaxed border-l-2 border-burgundy-300 pl-5',
            error: 'text-burgundy-100 text-xs uppercase tracking-widest2',
          }}
        />
      </div>
    </motion.div>
  );
}

function BuySection({ t, reduce, v }) {
  return (
    <motion.div className="grid grid-cols-12 gap-x-6 sm:gap-x-10 gap-y-10" {...fadeInView(reduce, 0.05)}>
      <div className="col-span-12 md:col-span-5">
        <h2
          className="font-noir text-silver-50 leading-[1.05] tracking-[-0.02em] text-balance"
          style={{ fontSize: 'clamp(1.9rem, 4.4vw, 3rem)' }}
        >
          {t('buy.heading')}
        </h2>
        <p className="mt-5 sm:mt-6 text-silver-300 text-[0.95rem] sm:text-base leading-relaxed text-pretty" style={{ maxWidth: '42ch' }}>
          {t('buy.body')}
        </p>
        {book.sample.enabled && (
          <a
            href={book.sample.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-7 inline-flex items-center gap-2 text-silver-300 hover:text-silver-50 text-[0.7rem] uppercase tracking-widest2 transition-colors"
          >
            <span>{t('hero.sample')}</span>
            <span aria-hidden className="inline-block transition-transform duration-500 ease-out-quart group-hover:translate-x-1">→</span>
          </a>
        )}
      </div>
      <div className="col-span-12 md:col-span-7">
        <ul className="divide-y divide-silver-500/15 border-t border-b border-silver-500/15">
          {book.retailers.map((r, i) => {
            const live = r.enabled;
            const inner = (
              <div className="flex items-center justify-between py-4 sm:py-5 gap-4 group transition-colors duration-300 hover:bg-silver-500/[0.04] -mx-2 px-2">
                <div className="flex items-baseline gap-4 sm:gap-6 min-w-0">
                  <span className="text-silver-500 text-[0.62rem] sm:text-[0.66rem] uppercase tracking-widest3 w-7 sm:w-10 shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="font-noir text-silver-50 text-xl sm:text-2xl tracking-tight truncate">{r.label}</span>
                </div>
                <span className="shrink-0 text-silver-400 text-[0.62rem] sm:text-[0.66rem] uppercase tracking-widest2 group-hover:text-silver-100 transition-colors flex items-center gap-2">
                  {live ? (
                    <>
                      <span>{t('buy.open')}</span>
                      <span aria-hidden className="transition-transform duration-500 ease-out-quart group-hover:translate-x-1">→</span>
                    </>
                  ) : (
                    <span>{t('buy.comingSoon')}</span>
                  )}
                </span>
              </div>
            );
            return (
              <li key={r.id}>
                {live ? (
                  <a href={r.url} target="_blank" rel="noopener noreferrer">{inner}</a>
                ) : (
                  <div aria-disabled className="opacity-70">{inner}</div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </motion.div>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer({ t, year, isGreek }) {
  return (
    <footer className="relative z-10 border-t border-silver-500/15 mt-24 sm:mt-32 px-3 sm:px-10 lg:px-20 xl:px-28 py-12 sm:py-14">
      <div className="max-w-[88rem] mx-auto grid grid-cols-12 gap-x-3 sm:gap-x-6 gap-y-10">
        <div className="col-span-12 md:col-span-6">
          <p className="font-trajan text-silver-50 text-3xl sm:text-4xl tracking-wide">Requiem.</p>
          <p className="font-noir italic text-silver-300 text-[0.95rem] sm:text-base mt-3 max-w-md text-pretty">
            {t('footer.tagline')}
          </p>
        </div>
        <div className="col-span-6 md:col-span-3">
          <p className="text-silver-500 text-[0.62rem] uppercase tracking-widest3 mb-3">{t('footer.follow')}</p>
          <SocialIcons size="lg" tone="burgundy" />
        </div>
        <div className="col-span-6 md:col-span-3">
          <p className="text-silver-500 text-[0.62rem] uppercase tracking-widest3 mb-3">{isGreek ? t('footer.contact') : 'Contact'}</p>
          <a
            href={`mailto:${book.contactEmail}`}
            className="text-silver-200 hover:text-silver-50 transition-colors text-[0.62rem] sm:text-xs whitespace-nowrap font-noirBody block overflow-hidden"
          >
            {book.contactEmail}
          </a>
        </div>
      </div>
      <div className="max-w-[88rem] mx-auto mt-10 sm:mt-12 pt-6 border-t border-silver-500/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-silver-500 text-[0.66rem] uppercase tracking-widest2">
        <span>{t('footer.copyright', { year })}</span>
        <a href="#top" className="hover:text-silver-200 transition-colors">↑ {t('nav.home')}</a>
      </div>
    </footer>
  );
}
